import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  AppState,
  UploadedImage,
  SegmentationMask,
  Finish,
  GlossLevel,
  AppError,
} from '@/types';

export const DEFAULT_FINISH: Finish = {
  id: 'porcelain-white',
  name: 'Porcelain White',
  category: 'white',
  patternId: 'solid',
  color: '#F5F5F0',
  labL: 96.5,
  labA: -0.5,
  labB: 3.0,
  glossFactors: {
    matte: { lightness: 0, saturation: 0 },
    satin: { lightness: 0, saturation: 0 },
    high: { lightness: 0, saturation: 0 },
  },
  isPremium: false,
};

interface VisualizerState {
  appState: AppState;
  error: AppError | null;
  uploadedImage: UploadedImage | null;
  segmentationMask: SegmentationMask | null;
  selectedFinish: Finish;
  selectedGloss: GlossLevel;
  includeBacksplash: boolean;
  beforeAfterRatio: number;
  compositeDataUrl: string | null;
  previewResultUrl: string | null;
  processingProgress: number;
  processingMessage: string;
  userEmail: string | null;
  userName: string | null;
  userPhone: string | null;
  userZip: string | null;
  beforeImagePublicUrl: string | null;

  // Actions
  setAppState: (state: AppState) => void;
  setImage: (image: UploadedImage) => void;
  setSegmentationResult: (mask: SegmentationMask, url: string) => void;
  setFinish: (finish: Finish) => void;
  setGloss: (gloss: GlossLevel) => void;
  setIncludeBacksplash: (enabled: boolean) => void;
  setBeforeAfterRatio: (ratio: number) => void;
  setProcessingProgress: (progress: number, message: string) => void;
  setPreviewResult: (url: string | null) => void;
  setError: (error: AppError | null) => void;
  setUserEmail: (email: string) => void;
  setUserContact: (contact: { name: string; phone: string; email: string; zipCode: string }) => void;
  setBeforeImagePublicUrl: (url: string | null) => void;
  reset: () => void;
}

function getSavedItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

const initialState = {
  appState: 'IDLE' as AppState,
  error: null,
  uploadedImage: null,
  segmentationMask: null,
  selectedFinish: DEFAULT_FINISH,
  selectedGloss: 'high' as GlossLevel,
  includeBacksplash: false,
  beforeAfterRatio: 0.5,
  compositeDataUrl: null,
  previewResultUrl: null,
  processingProgress: 0,
  processingMessage: '',
  userEmail: getSavedItem('cv_user_email'),
  userName: getSavedItem('cv_user_name'),
  userPhone: getSavedItem('cv_user_phone'),
  userZip: getSavedItem('cv_user_zip'),
  beforeImagePublicUrl: null,
};

export const useVisualizerStore = create<VisualizerState>()(
  immer((set) => ({
    ...initialState,

    setAppState: (state) =>
      set((draft) => {
        draft.appState = state;
      }),

    setImage: (image) =>
      set((draft) => {
        // Revoke previous object URL to prevent memory leaks
        if (draft.uploadedImage?.sourceUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(draft.uploadedImage.sourceUrl);
        }
        draft.uploadedImage = image;
        draft.appState = 'CONFIGURING';
        draft.previewResultUrl = null;
      }),

    setSegmentationResult: (mask, url) =>
      set((draft) => {
        draft.segmentationMask = mask;
        draft.compositeDataUrl = url;
        draft.appState = 'PREVIEW';
        draft.processingProgress = 1;
        draft.processingMessage = 'Done!';
      }),

    setFinish: (finish) =>
      set((draft) => {
        draft.selectedFinish = finish;
      }),

    setGloss: (gloss) =>
      set((draft) => {
        draft.selectedGloss = gloss;
      }),

    setIncludeBacksplash: (enabled) =>
      set((draft) => {
        draft.includeBacksplash = enabled;
      }),

    setBeforeAfterRatio: (ratio) =>
      set((draft) => {
        draft.beforeAfterRatio = ratio;
      }),

    setProcessingProgress: (progress, message) =>
      set((draft) => {
        draft.processingProgress = progress;
        draft.processingMessage = message;
      }),

    setPreviewResult: (url) =>
      set((draft) => {
        draft.previewResultUrl = url;
      }),

    setError: (error) =>
      set((draft) => {
        draft.error = error;
        if (error) {
          draft.appState = 'ERROR';
        }
        // When clearing an error, leave appState as-is (e.g. ERROR).
        // The retry action will set it to MODEL_LOADING explicitly.
      }),

    setUserEmail: (email) =>
      set((draft) => {
        draft.userEmail = email;
        try {
          localStorage.setItem('cv_user_email', email);
        } catch {
          // ignore storage errors
        }
      }),

    setUserContact: (contact) =>
      set((draft) => {
        draft.userName = contact.name;
        draft.userPhone = contact.phone;
        draft.userEmail = contact.email;
        draft.userZip = contact.zipCode;
        try {
          localStorage.setItem('cv_user_name', contact.name);
          localStorage.setItem('cv_user_phone', contact.phone);
          localStorage.setItem('cv_user_email', contact.email);
          localStorage.setItem('cv_user_zip', contact.zipCode);
        } catch {
          // ignore storage errors
        }
      }),

    setBeforeImagePublicUrl: (url) =>
      set((draft) => {
        draft.beforeImagePublicUrl = url;
      }),

    reset: () =>
      set((draft) => {
        // Revoke any blob URLs before resetting state
        if (draft.uploadedImage?.sourceUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(draft.uploadedImage.sourceUrl);
        }
        // Deep clone initialState to avoid sharing nested references across resets
        const cloned = JSON.parse(JSON.stringify(initialState)) as typeof initialState;
        // Preserve user contact info across resets; clear everything else
        const savedEmail = draft.userEmail;
        const savedName = draft.userName;
        const savedPhone = draft.userPhone;
        const savedZip = draft.userZip;
        Object.keys(draft).forEach((key) => {
          delete (draft as Record<string, unknown>)[key];
        });
        Object.assign(draft, cloned);
        draft.userEmail = savedEmail;
        draft.userName = savedName;
        draft.userPhone = savedPhone;
        draft.userZip = savedZip;
      }),
  }))
);

// Selector hooks for performance
export const useAppState = () => useVisualizerStore((s) => s.appState);
export const useSelectedFinish = () => useVisualizerStore((s) => s.selectedFinish);
export const useSelectedGloss = () => useVisualizerStore((s) => s.selectedGloss);
export const useCanvasRenderParams = () =>
  useVisualizerStore((s) => ({
    finish: s.selectedFinish,
    glossLevel: s.selectedGloss,
    includeBacksplash: s.includeBacksplash,
    beforeAfterRatio: s.beforeAfterRatio,
  }));
